import { callable } from '../callable';

export type NotificationChannel = 'in-app' | 'email' | 'push';
export type NotificationType =
  | 'assignment.published'
  | 'assignment.due-soon'
  | 'assignment.overdue'
  | 'submission.submitted'
  | 'submission.graded'
  | 'submission.returned'
  | 'announcement'
  | 'system';
export type NotificationStatus = 'unread' | 'read';

export interface NotificationPayloadDto {
  title: string;
  body: string;
  actionUrl?: string;
  data?: Record<string, string | number | boolean | null>;
}

export interface NotificationDto {
  id: string;
  type: NotificationType;
  payload: NotificationPayloadDto;
  status: NotificationStatus;
  createdAt: string;
  readAt?: string;
}

export interface NotificationPreferencesDto {
  disabledChannels: NotificationChannel[];
  optedOutTypes: NotificationType[];
  pushTokensCount: number;
}

export const notificationApi = {
  list: async (data: {
    tenantId: string;
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<{ items: NotificationDto[] }> => {
    const fn = callable<typeof data, { items: NotificationDto[] }>('listNotifications');
    return (await fn(data)).data;
  },
  countUnread: async (data: { tenantId: string }): Promise<{ count: number }> => {
    const fn = callable<typeof data, { count: number }>('countUnreadNotifications');
    return (await fn(data)).data;
  },
  markRead: async (data: {
    tenantId: string;
    notificationId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('markNotificationRead');
    return (await fn(data)).data;
  },
  markAllRead: async (data: { tenantId: string }): Promise<{ updated: number }> => {
    const fn = callable<typeof data, { updated: number }>('markAllNotificationsRead');
    return (await fn(data)).data;
  },
  getPreferences: async (data: { tenantId: string }): Promise<NotificationPreferencesDto> => {
    const fn = callable<typeof data, NotificationPreferencesDto>('getNotificationPreferences');
    return (await fn(data)).data;
  },
  updatePreferences: async (data: {
    tenantId: string;
    disabledChannels: NotificationChannel[];
    optedOutTypes: NotificationType[];
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateNotificationPreferences');
    return (await fn(data)).data;
  },
  registerPushToken: async (data: {
    tenantId: string;
    token: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('registerPushToken');
    return (await fn(data)).data;
  },
  unregisterPushToken: async (data: {
    tenantId: string;
    token: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('unregisterPushToken');
    return (await fn(data)).data;
  },
};
